// v86 engine: a 32-bit x86 PC emulated in this page (BSD-2, runs anywhere, no special headers).
// Ported from EndisOS src/linux/v86/adapter.ts. Re:Vault specifics:
//   - runtime + BIOS come from cloaked /dist URLs (build.mjs), loaded only when a machine boots;
//   - the pinned Alpine ISO is served by Re:Vault itself with HTTP Range, so it is streamed
//     ({ url, async: true, size }) instead of downloaded up front; uploads come from IndexedDB;
//   - networking uses Re:Vault's Linux-only wisp relay, DNS goes to the same-origin resolver;
//   - snapshots (with a compatibility identity) live in IndexedDB, quota-checked before writing.
import { LinuxError, toLinuxError, listeners, withAbort, sleep } from "../engine.js";
import { acquireMachineLock } from "../lock.js";
import { wispRelay, installDohShim, DOH_SENTINEL } from "./network.js";
import { catalogImage, imageKey } from "../../shared/linux-catalog.js";
import { fitsInQuota } from "../../shared/linux-store.js";

export const GUEST_SHARE_MOUNT = "/mnt/host";
export const GUEST_MOUNT_COMMAND = "mkdir -p " + GUEST_SHARE_MOUNT + " && modprobe 9pnet_virtio 2>/dev/null; mount -t 9p -o trans=virtio,version=9p2000.L host9p " + GUEST_SHARE_MOUNT;
export const GUEST_NETWORK_COMMAND = "ip link set eth0 up && udhcpc -i eth0";
// Images at or below this size are handed to v86 whole; larger uploads are read lazily in slices.
export const SYNC_IMAGE_LIMIT = 64 * 1024 * 1024;
const VGA_MEMORY = 8 * 1024 * 1024;
// Server-streamed images are read in aligned, cached chunks. Without this v86 fetches every tiny
// BIOS/kernel read as its own Range request (thousands of round trips while booting).
export const STREAM_CHUNK = 256 * 1024;
const IMAGE_WAIT_MS = 5 * 60 * 1000;

export const CAPABILITIES = Object.freeze({ id: "v86", name: "v86", networking: "wisp", graphics: true, resizableConsole: true });

// Keeps a 9p share name to one path component without control characters.
export function safeShareName(name) {
	const parts = String(name || "").split(/[\\/]/).filter(Boolean);
	const cleaned = (parts[parts.length - 1] || "").replace(/[\u0000-\u001f\u007f]/g, "");
	if (!cleaned || cleaned === "." || cleaned === "..") return "file";
	return cleaned.slice(0, 255);
}
export const imageKindFromName = (name) => (/\.iso$/i.test(name) ? "iso" : "hda");

// A snapshot is only valid for the exact disk + memory it was taken with. Anything else would
// resume a guest against different hardware, so a mismatch refuses to boot and KEEPS the snapshot.
export function snapshotIdentity(machine, image) {
	return JSON.stringify({ v: 1, image: machine.image, memory: machine.memory, size: image ? image.size : 0, added: image && image.addedAt ? image.addedAt : 0 });
}

// Destroys an emulator that was abandoned before "emulator-loaded" (destroy() before then throws,
// and the half-built instance would otherwise finish initialising and keep running in the background).
export function disposeWhenLoaded(emulator) {
	if (emulator.__rvDisposing) return;
	emulator.__rvDisposing = true;
	const kill = () => {
		if (emulator.network_adapter && emulator.network_adapter.register_ws) emulator.network_adapter.register_ws = () => {};
		Promise.resolve().then(() => emulator.destroy()).catch(() => {});
	};
	if (emulator.__rvLoaded) kill();
	else emulator.add_listener("emulator-loaded", () => { emulator.__rvLoaded = true; kill(); });
}

export class V86Engine {
	// host: { urls: { script, wasm, wasmFallback, bios, vgaBios }, imagePrefix, wispUrl, dohUrl,
	//   store: { getSnapshot, putSnapshot, deleteSnapshot, getImage }, estimate(), secure,
	//   loadRuntime?, fetchImpl?, acquireLease?, startupTimeoutMs?, log? }
	constructor(host) {
		this.host = host;
		this.capabilities = CAPABILITIES;
		this.stateValue = "idle";
		this.errorValue = null;
		this.emulator = null;
		this.machine = null;
		this.output = null;
		this.runtime = null;
		this.states = listeners();
		this.activities = listeners();
		this.displays = listeners();
		this.progress = listeners();
		this.serialQueue = [];
		this.serialScheduled = false;
		this.serialBytes = 0;
		this.suspended = false;
		this.tabVisible = true;
		this.captured = false;
		this.diskBusy = false;
		this.lastInstructions = 0;
		this.activityTimer = null;
		this.screenSize = { width: 0, height: 0, graphical: false };
		this.release = null;
		this.starting = null;
		this.startFlight = null;
		this.stopping = null;
		this.snapshotFlight = null;
		this.disposed = false;
		const doc = host.document || document;
		this.screen = doc.createElement("div");
		this.screen.className = "rv-lx-v86-screen";
		this.screen.append(doc.createElement("div"), doc.createElement("canvas"));
		this.onVisibility = () => this.applyVisibility();
		doc.addEventListener("visibilitychange", this.onVisibility);
	}

	get state() { return this.stateValue; }
	get error() { return this.errorValue; }
	get currentMachine() { return this.machine; }
	onState(fn) { return this.states.add(fn); }
	onActivity(fn) { return this.activities.add(fn); }
	onDisplay(fn) { return this.displays.add(fn); }
	onProgress(fn) { return this.progress.add(fn); }

	async start(machine, console, signal) {
		if (this.disposed) throw new LinuxError("ABORTED", "This Linux tab has closed.");
		if (this.startFlight) throw new LinuxError("PROCESS_FAILED", "The machine is already starting.");
		const controller = new AbortController();
		this.starting = controller;
		const abort = () => controller.abort();
		if (signal.aborted) abort(); else signal.addEventListener("abort", abort, { once: true });
		// Covers everything up to "running"; the image wait inside has its own, longer deadline.
		const timer = setTimeout(abort, this.host.startupTimeoutMs || IMAGE_WAIT_MS + 60000);
		const flight = this.startMachine(machine, console, controller.signal);
		this.startFlight = flight;
		try { await flight; }
		finally { clearTimeout(timer); signal.removeEventListener("abort", abort); this.starting = null; this.startFlight = null; }
	}

	async startMachine(machine, console, signal) {
		if (this.emulator || !["idle", "stopped", "error"].includes(this.stateValue)) throw new LinuxError("PROCESS_FAILED", "The machine is already running.");
		if (machine.engine !== "v86") throw new LinuxError("PROCESS_FAILED", machine.name + " is not a v86 machine.");
		this.machine = structuredClone(machine);
		this.output = console;
		this.errorValue = null;
		this.serialBytes = 0;
		try {
			this.setState("checking");
			if (signal.aborted) throw new LinuxError("ABORTED", "Start cancelled.");
			this.release = await (this.host.acquireLease || acquireMachineLock)(machine.id, signal);
			this.setState("loading-runtime");
			const module = await withAbort(this.loadRuntime(), signal);
			this.setState("opening-disk");
			const disk = await withAbort(this.resolveImage(machine, signal), signal);
			const initial = machine.persist ? await withAbort(this.readSnapshot(machine, disk), signal) : null;
			this.setState("booting");
			const options = this.buildOptions(machine, disk, initial, signal);
			const emulator = await this.construct(module, options, signal);
			this.emulator = emulator;
			this.attachListeners(emulator);
			emulator.keyboard_set_enabled(false);
			emulator.mouse_set_enabled(false);
			await this.run(emulator, signal);
			if (this.disposed) throw new LinuxError("ABORTED", "This Linux tab has closed.");
			this.setState("running");
			this.startActivity(emulator);
			this.applyVisibility();
		} catch (err) {
			const failure = err instanceof LinuxError ? err : toLinuxError(signal.aborted ? "ABORTED" : "RUNTIME_INIT_FAILED", err, "Could not start the machine");
			await this.teardown();
			this.errorValue = { code: failure.code, message: failure.message };
			this.setState("error");
			throw failure;
		}
	}

	buildOptions(machine, disk, initial, signal) {
		const urls = this.host.urls;
		let wasmBytes = null;
		const fetchImpl = this.host.fetchImpl || fetch;
		const options = {
			// Our own loader so the cloaked wasm names work (v86 would derive "v86-fallback.wasm"
			// from the primary name). The fallback build is for CPUs/browsers without some wasm features.
			// v86 has no rejection handler for wasm_fn, so a failure is reported through wasmFailed
			// (construct() rejects at once), and after an abort the promise never settles, so the
			// half-built emulator never starts its worker or network adapter.
			wasm_fn: async (env) => {
				const load = async (u) => {
					const res = await fetchImpl(u);
					if (!res.ok) throw new Error("HTTP " + res.status);
					const bytes = await res.arrayBuffer();
					const { instance } = await WebAssembly.instantiate(bytes, env);
					wasmBytes = bytes;
					return instance.exports;
				};
				let exports;
				try { exports = await load(urls.wasm); }
				catch (e) {
					try { exports = await load(urls.wasmFallback); }
					catch (e2) { if (this.wasmFailed) this.wasmFailed(e2); return new Promise(() => {}); }
				}
				if (signal && signal.aborted) return new Promise(() => {});
				return exports;
			},
			memory_size: machine.memory * 1024 * 1024,
			acpi: !!machine.acpi,
			vga_memory_size: VGA_MEMORY,
			bios: { url: urls.bios },
			vga_bios: { url: urls.vgaBios },
			screen_container: this.screen,
			filesystem: {}, // enables the 9p share (tag host9p)
			autostart: false,
			disable_speaker: true,
		};
		this.wasmBytes = () => wasmBytes;
		if (disk && disk.kind === "iso") options.cdrom = disk.image;
		else if (disk) options.hda = disk.image;
		const relay = machine.network !== false && this.host.wispUrl ? wispRelay(this.host.wispUrl, this.host.secure) : undefined;
		if (relay) {
			options.network_relay_url = relay;
			if (this.host.dohUrl) {
				installDohShim(this.host.dohUrl);
				options.net_device = { type: "ne2k", dns_method: "doh", doh_server: DOH_SENTINEL };
			}
		}
		if (initial) options.initial_state = { buffer: initial };
		return options;
	}

	sendInput(bytes) {
		if (this.emulator && this.stateValue === "running") this.emulator.serial_send_bytes(0, bytes);
	}
	resize() {} // the serial console has no size; the guest learns it from stty

	// Graceful stop. Saves a snapshot first when the machine persists. If saving fails the VM keeps
	// running and the error is rethrown: never destroy the only copy of the user's work.
	async stop(save = true) {
		if (this.startFlight) { if (this.starting) this.starting.abort(); await this.startFlight.catch(() => {}); }
		if (this.stopping) return this.stopping;
		if (!this.emulator) { if (this.stateValue !== "error") this.setState("stopped"); return; }
		this.stopping = (async () => {
			this.setState("stopping");
			try {
				if (save && this.machine && this.machine.persist) await this.saveSnapshot();
			} catch (err) {
				this.setState("running");
				this.applyVisibility(); // hidden during the failed save? pause again
				throw err;
			}
			await this.teardown();
			this.setState("stopped");
		})();
		try { await this.stopping; } finally { this.stopping = null; }
	}

	reboot() { if (this.emulator) this.emulator.restart(); }

	saveSnapshot() {
		if (this.snapshotFlight) return this.snapshotFlight;
		this.snapshotFlight = this.writeSnapshot().finally(() => { this.snapshotFlight = null; this.applyVisibility(); });
		return this.snapshotFlight;
	}

	async writeSnapshot() {
		const emulator = this.emulator, machine = this.machine;
		if (!emulator || !machine) throw new LinuxError("PROCESS_FAILED", "Nothing is running to save.");
		const wasRunning = emulator.is_running();
		if (wasRunning) await emulator.stop();
		try {
			const state = await emulator.save_state();
			const est = this.host.estimate ? await this.host.estimate() : null;
			if (!fitsInQuota(est, state.byteLength)) {
				throw new LinuxError("STORAGE_FAILED", "Not enough browser storage for a " + Math.round(state.byteLength / 1048576) + " MB snapshot. Free space in Settings > Advanced > Linux machines (or turn off \"Save state\" for this machine), then try again. The machine is still running.");
			}
			const image = await this.imageInfo(machine).catch(() => null);
			try {
				await this.host.store.putSnapshot(machine.id, { data: new Blob([state], { type: "application/octet-stream" }), meta: snapshotIdentity(machine, image), bytes: state.byteLength, savedAt: Date.now() });
			} catch (err) {
				throw toLinuxError("STORAGE_FAILED", err, "Could not write the snapshot (the machine is still running)");
			}
			return { bytes: state.byteLength };
		} finally {
			// Resume only if the tab is still visible (it may have been hidden during a long save).
			const doc = this.host.document || document;
			const visible = this.tabVisible && !doc.hidden;
			if (wasRunning && visible && !this.suspended && this.emulator === emulator && !this.disposed) await emulator.run();
			else if (wasRunning && !visible && this.emulator === emulator) this.suspended = true;
		}
	}

	// Pauses the CPU while the page is hidden (browser tab in the background, or this Re:Vault
	// tab not selected - the app tells us via setTabVisible).
	setTabVisible(visible) { this.tabVisible = !!visible; this.applyVisibility(); }
	applyVisibility() {
		const emulator = this.emulator;
		if (!emulator || this.stateValue !== "running" || this.snapshotFlight) return;
		const doc = this.host.document || document;
		const visible = this.tabVisible && !doc.hidden;
		if (!visible && !this.suspended) { this.suspended = true; emulator.stop(); this.setCapture(false); }
		else if (visible && this.suspended) { this.suspended = false; emulator.run(); }
		this.activities.emit(this.snapshotActivity(false));
	}

	// Keyboard/mouse reach the guest only while captured (the Screen tab after a click).
	setCapture(on) {
		this.captured = !!on && !!this.emulator;
		if (this.emulator) { this.emulator.keyboard_set_enabled(this.captured); this.emulator.mouse_set_enabled(this.captured); }
	}
	lockMouse() { if (this.emulator) Promise.resolve(this.emulator.lock_mouse()).catch(() => {}); }

	// Scales the VGA output to fit the given CSS box (uniform, 0.25x..4x).
	fitScreen(width, height) {
		const emulator = this.emulator;
		if (!emulator || width <= 0 || height <= 0) return 1;
		emulator.screen_set_scale(1, 1);
		// Measure whichever surface v86 is showing: it also draws TEXT modes on the canvas
		// ("graphical text"), so the reported bpp doesn't say which element is visible.
		const shown = (el) => el && el.style.display !== "none" && el.getBoundingClientRect().width > 0;
		const canvas = this.screen.querySelector("canvas"), text = this.screen.querySelector("div");
		const target = shown(canvas) ? canvas : shown(text) ? text : null;
		const rect = target && target.getBoundingClientRect();
		if (!rect || !rect.width || !rect.height) return 1;
		const scale = Math.max(0.25, Math.min(4, width / rect.width, height / rect.height));
		emulator.screen_set_scale(scale, scale);
		return scale;
	}

	async importFile(name, blob) {
		const emulator = this.emulator;
		if (!emulator || this.stateValue !== "running") throw new LinuxError("PROCESS_FAILED", "Start the machine before sending files to it.");
		const safe = safeShareName(name);
		await emulator.create_file("/" + safe, new Uint8Array(await blob.arrayBuffer()));
		return GUEST_SHARE_MOUNT + "/" + safe;
	}
	async exportFile(name) {
		const emulator = this.emulator;
		if (!emulator) throw new LinuxError("PROCESS_FAILED", "Start the machine before downloading its files.");
		try { return new Blob([await emulator.read_file("/" + safeShareName(name))], { type: "application/octet-stream" }); }
		catch (e) { return null; }
	}
	async listExports() {
		const fs = this.emulator && this.emulator.fs9p;
		const names = fs && typeof fs.read_dir === "function" ? fs.read_dir("/") : null;
		return names ? [...names].filter((n) => n !== "." && n !== "..").sort() : [];
	}

	// Page is closing: release everything without async writes (they can't finish in pagehide).
	async dispose() {
		this.disposed = true;
		if (this.starting) this.starting.abort();
		(this.host.document || document).removeEventListener("visibilitychange", this.onVisibility);
		await this.teardown();
		this.states.clear(); this.activities.clear(); this.displays.clear(); this.progress.clear();
	}

	// --- internals -----------------------------------------------------------

	setState(s) {
		if (this.stateValue === s) return;
		this.stateValue = s;
		this.states.emit(s);
	}

	loadRuntime() {
		if (!this.runtime) {
			const load = this.host.loadRuntime || (() => import(this.host.urls.script));
			this.runtime = load().then(
				(m) => {
					if (typeof (m && (m.default || m.V86)) !== "function") throw new LinuxError("RUNTIME_LOAD_FAILED", "The v86 runtime did not load correctly.");
					return m;
				},
				(err) => { this.runtime = null; throw toLinuxError("RUNTIME_LOAD_FAILED", err, "Could not load the v86 runtime (check your connection and reload)"); }
			);
		}
		return this.runtime;
	}

	async imageInfo(machine) {
		const [kind, id] = machine.image.split(":");
		if (kind === "catalog") { const img = catalogImage(id); return img ? { size: img.size } : null; }
		if (kind === "upload") { const rec = await this.host.store.getImage(id); return rec ? { size: rec.size, addedAt: rec.addedAt } : null; }
		return null;
	}

	async resolveImage(machine, signal) {
		if (!machine.image) return null;
		const [kind, id] = machine.image.split(":");
		if (kind === "upload") {
			let rec;
			try { rec = await this.host.store.getImage(id); } catch (e) { throw toLinuxError("STORAGE_FAILED", e, "Could not read the uploaded image"); }
			if (!rec || !rec.blob) throw new LinuxError("IMAGE_UNREACHABLE", "The uploaded image for " + machine.name + " is gone from this browser. Upload it again in the Machine tab.");
			const file = new File([rec.blob], rec.name || "disk.img", { type: "application/octet-stream" });
			return { kind: imageKindFromName(file.name), image: { buffer: file, async: rec.size > SYNC_IMAGE_LIMIT }, name: file.name, size: rec.size };
		}
		const img = catalogImage(id);
		if (kind !== "catalog" || !img) throw new LinuxError("IMAGE_INVALID", "Unknown disk image \"" + machine.image + "\".");
		if (!this.host.imagePrefix) throw new LinuxError("IMAGE_UNREACHABLE", "This Re:Vault server has no Linux image route yet (it needs a rebuild).");
		await this.waitForServerImage(img, signal);
		return { kind: img.kind, image: { url: this.host.imagePrefix + "i/" + imageKey(img), async: true, size: img.size, fixed_chunk_size: STREAM_CHUNK }, name: img.name, size: img.size };
	}

	// The server fetches the pinned image once (sha256-verified) and then serves it forever;
	// the first boot after a deploy may wait a few seconds for that, with visible progress.
	async waitForServerImage(img, signal) {
		const fetchImpl = this.host.fetchImpl || fetch;
		const url = this.host.imagePrefix + "s/" + imageKey(img);
		const deadline = Date.now() + IMAGE_WAIT_MS;
		for (;;) {
			if (signal.aborted) throw new LinuxError("ABORTED", "Start cancelled.");
			let st;
			try {
				const res = await withAbort(fetchImpl(url, { cache: "no-store" }), signal);
				if (!res.ok) throw new Error("HTTP " + res.status);
				st = await res.json();
			} catch (err) {
				if (err instanceof LinuxError) throw err;
				throw toLinuxError("IMAGE_UNREACHABLE", err, "Re:Vault couldn't prepare " + img.name + " (check your connection)");
			}
			this.progress.emit({ stage: "image", loaded: st.received || 0, total: st.size || img.size, ready: st.state === "ready" });
			if (st.state === "ready") return;
			if (st.state === "error") throw new LinuxError("IMAGE_UNREACHABLE", "The server couldn't download " + img.name + " right now (" + (st.error || "unknown error") + "). Try again in a minute.");
			if (Date.now() > deadline) throw new LinuxError("IMAGE_UNREACHABLE", "Preparing " + img.name + " is taking too long. Try again in a minute.");
			await withAbort(sleep(800), signal);
		}
	}

	async readSnapshot(machine, disk) {
		let rec;
		try { rec = await this.host.store.getSnapshot(machine.id); }
		catch (err) { throw toLinuxError("STORAGE_FAILED", err, "Could not read this machine's saved state (it has been kept)"); }
		if (!rec) return null;
		const image = await this.imageInfo(machine).catch(() => null);
		if (rec.meta !== snapshotIdentity(machine, image || (disk ? { size: disk.size } : null))) {
			throw new LinuxError("IMAGE_INVALID", "This machine's saved state was made with a different disk or memory size, so it can't resume. Put the old setting back, or choose \"Discard saved state\" in the Machine tab.");
		}
		try { return await rec.data.arrayBuffer(); }
		catch (err) { throw toLinuxError("STORAGE_FAILED", err, "Could not read this machine's saved state (it has been kept)"); }
	}

	construct(module, options, signal) {
		if (signal.aborted) return Promise.reject(new LinuxError("ABORTED", "Start cancelled."));
		const Ctor = module.default || module.V86;
		return new Promise((resolve, reject) => {
			let emulator;
			const cleanup = () => {
				if (emulator) { emulator.remove_listener("emulator-loaded", onLoaded); emulator.remove_listener("download-error", onError); emulator.remove_listener("download-progress", onProgress); }
				signal.removeEventListener("abort", onAbort);
			};
			const onLoaded = () => { emulator.__rvLoaded = true; cleanup(); if (this.wasmBytes && this.wasmBytes()) { try { emulator.wasm_source = this.wasmBytes().slice(0); } catch (e) {} } resolve(emulator); };
			const onError = (info) => { cleanup(); if (emulator) disposeWhenLoaded(emulator); reject(new LinuxError(/bios/i.test(info.file_name || "") ? "RUNTIME_INIT_FAILED" : "IMAGE_UNREACHABLE", "v86 could not load " + (info.file_name || "a file") + ".")); };
			const onProgress = (p) => this.progress.emit({ stage: "runtime", loaded: p.loaded, total: p.lengthComputable ? p.total : 0, file: p.file_name });
			this.wasmFailed = (err) => { cleanup(); reject(toLinuxError("RUNTIME_LOAD_FAILED", err, "Could not load the emulator (check your connection; some networks block large downloads)")); };
			// v86 keeps initialising after we give up (wasm, network adapter, key listeners), so destroy
			// it as soon as it finishes loading instead of leaking a live emulator.
			const onAbort = () => { cleanup(); if (emulator) disposeWhenLoaded(emulator); reject(new LinuxError("ABORTED", "Start cancelled or timed out.")); };
			try { emulator = new Ctor(options); this.emulator = emulator; }
			catch (err) { reject(toLinuxError("RUNTIME_INIT_FAILED", err, "v86 could not start (WebAssembly may be blocked)")); return; }
			emulator.add_listener("emulator-loaded", onLoaded);
			emulator.add_listener("download-error", onError);
			emulator.add_listener("download-progress", onProgress);
			signal.addEventListener("abort", onAbort, { once: true });
		});
	}

	run(emulator, signal) {
		if (signal.aborted) return Promise.reject(new LinuxError("ABORTED", "Start cancelled."));
		return new Promise((resolve, reject) => {
			const cleanup = () => { emulator.remove_listener("emulator-started", onStarted); signal.removeEventListener("abort", onAbort); };
			const onStarted = () => { cleanup(); resolve(); };
			const onAbort = () => { cleanup(); reject(new LinuxError("ABORTED", "Start cancelled.")); };
			emulator.add_listener("emulator-started", onStarted);
			signal.addEventListener("abort", onAbort, { once: true });
			Promise.resolve(emulator.run()).catch((err) => { cleanup(); reject(toLinuxError("RUNTIME_INIT_FAILED", err, "The emulator refused to run")); });
		});
	}

	attachListeners(emulator) {
		emulator.add_listener("serial0-output-byte", (b) => this.queueSerial(b));
		emulator.add_listener("screen-set-size", (size) => {
			const [width, height, bpp] = size;
			this.screenSize = { width, height, graphical: bpp !== 0 };
			this.displays.emit(this.screenSize);
		});
		emulator.add_listener("ide-read-start", () => { this.diskBusy = true; });
		emulator.add_listener("ide-read-end", () => { this.diskBusy = false; });
		emulator.add_listener("ide-write-end", () => { this.diskBusy = false; });
	}

	queueSerial(byte) {
		this.serialQueue.push(byte);
		this.serialBytes++;
		if (this.serialScheduled) return;
		this.serialScheduled = true;
		queueMicrotask(() => {
			this.serialScheduled = false;
			const bytes = Uint8Array.from(this.serialQueue);
			this.serialQueue = [];
			try { if (this.output) this.output.write(bytes); } catch (e) {}
		});
	}

	snapshotActivity(busy) {
		return { cpu: busy ? "busy" : this.suspended ? "paused" : "idle", disk: this.diskBusy ? "busy" : "idle", serialBytes: this.serialBytes, suspended: this.suspended };
	}

	startActivity(emulator) {
		this.lastInstructions = emulator.get_instruction_counter();
		this.activityTimer = setInterval(() => {
			if (this.emulator !== emulator) return;
			const now = emulator.get_instruction_counter();
			const busy = now !== this.lastInstructions && !this.suspended;
			this.lastInstructions = now;
			this.activities.emit(this.snapshotActivity(busy));
		}, 1000);
	}

	async teardown() {
		const emulator = this.emulator;
		this.emulator = null;
		if (emulator && !emulator.__rvLoaded) { disposeWhenLoaded(emulator); if (this.release) { try { this.release(); } catch (e) {} this.release = null; } this.output = null; return; }
		if (this.activityTimer) { clearInterval(this.activityTimer); this.activityTimer = null; }
		this.suspended = false; this.captured = false; this.diskBusy = false;
		this.serialQueue = [];
		if (emulator) {
			// A dropped Wisp socket schedules a reconnect 10 s later that calls back into this
			// instance; neutralise it BEFORE destroy (pinned 0.5.461 does not cancel it).
			if (emulator.network_adapter && emulator.network_adapter.register_ws) emulator.network_adapter.register_ws = () => {};
			try { emulator.keyboard_set_enabled(false); emulator.mouse_set_enabled(false); await emulator.destroy(); }
			catch (e) {}
		}
		if (this.release) { try { this.release(); } catch (e) {} this.release = null; }
		this.output = null;
	}
}
